import { cn, NoddiIcon } from 'noddi-ui-common';
import { commonTexts } from 'noddi-util';
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import { useRef } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '../../base/dropdown-menu';
import { selectionControlItemVariants, selectionControlListVariants } from '../../base/selectionControlVariants';
import { useOverflowVisibleCount } from './useOverflowVisibleCount';

type Option<T extends string> = {
  label: string;
  value: T;
  disabled?: boolean;
};

type Props<T extends string> = {
  options: readonly Option<T>[];
  value: T;
  onValueChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
  /** Labels the control renders itself. English by default; the customer app passes its Lingui strings. */
  translations?: { more: string };
};

/**
 * A compact single-choice control for switching state within one view. Keeps one line: segments
 * that do not fit move into a trailing "More" menu, the same way `NoddiTabs` overflows.
 */
export const NoddiSegmentedControl = <T extends string>({
  options,
  value,
  onValueChange,
  ariaLabel,
  className,
  translations = commonTexts.tabs
}: Props<T>) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const visibleCount = useOverflowVisibleCount({ itemCount: options.length, rowRef, measureRef }) ?? options.length;
  const visibleOptions = options.slice(0, visibleCount);
  const overflowOptions = options.slice(visibleCount);
  const selectedOverflowOption = overflowOptions.find((option) => option.value === value);

  // One node for the real trigger and its hidden twin, so the two can never measure differently.
  const overflowTriggerContent = (
    <>
      <span>{translations.more}</span>
      <NoddiIcon size='medium' name='ChevronDown' />
    </>
  );
  const overflowSeparator = (
    <span aria-hidden className='flex w-3 flex-none justify-center'>
      <span className='h-4 w-px bg-grey/40' />
    </span>
  );

  return (
    // Sized as min(every segment, the space available): the in-flow twin below gives the wrapper the
    // width of every segment whatever the visible row shows, so collapsing never shrinks the space the
    // next measurement sees.
    <div ref={rowRef} className={cn('w-fit max-w-full min-w-0', className)}>
      {/* One pill, two controls: the radio group holds only radios, so arrow keys never land on the
          menu button, which is reached with Tab instead. */}
      <div className={cn(selectionControlListVariants(), 'w-full flex-nowrap')}>
        <RadioGroupPrimitive.Root
          aria-label={ariaLabel}
          value={value}
          onValueChange={(next) => onValueChange(next as T)}
          className='flex min-w-0 flex-1'
        >
          {visibleOptions.map((option) => (
            <RadioGroupPrimitive.Item
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={selectionControlItemVariants({ selectedState: 'checked' })}
            >
              {option.label}
            </RadioGroupPrimitive.Item>
          ))}
        </RadioGroupPrimitive.Root>
        {overflowOptions.length > 0 && (
          // Switching a segment must not lock page scrolling, same as the tab overflow menu.
          <DropdownMenu modal={false}>
            {visibleOptions.length > 0 && overflowSeparator}
            <DropdownMenuTrigger
              // No radio is checked while the selection sits in the menu, so the button names it.
              aria-label={selectedOverflowOption ? `${translations.more}: ${selectedOverflowOption.label}` : undefined}
              // Radix owns `data-state` on a menu trigger (open/closed), so the checked styling comes
              // from `selected` instead of the state attribute.
              className={cn(selectionControlItemVariants({ selected: !!selectedOverflowOption }), 'flex-none')}
            >
              {overflowTriggerContent}
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuRadioGroup value={value} onValueChange={(next) => onValueChange(next as T)}>
                {overflowOptions.map((option) => (
                  <DropdownMenuRadioItem key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div aria-hidden className='pointer-events-none invisible h-0 overflow-hidden'>
        <div ref={measureRef} className={cn(selectionControlListVariants(), 'relative w-max flex-nowrap')}>
          {options.map((option) => (
            // `flex-none` where the real segment grows: the row is measured for what each segment
            // needs, not for how the list hands out leftover space.
            <button
              key={option.value}
              type='button'
              tabIndex={-1}
              className={cn(selectionControlItemVariants(), 'flex-none')}
            >
              {option.label}
            </button>
          ))}
          {/* Out of flow, so the menu twin does not widen the wrapper when everything fits. */}
          <span className='absolute flex w-max'>
            {overflowSeparator}
            <button type='button' tabIndex={-1} className={cn(selectionControlItemVariants(), 'flex-none')}>
              {overflowTriggerContent}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};
